
import { Navbar } from '@/components/Navbar';
import { Helmet } from 'react-helmet-async';
import { Footer } from '@/components/layout/Footer';
import { Link } from 'react-router-dom';
import { 
  Heart, 
  HardHat, 
  Scale, 
  Laptop, 
  Cog, 
  ChefHat, 
  Store, 
  Users 
} from 'lucide-react';

const Industries = () => {
  const industries = [
    {
      name: 'Healthcare',
      description: 'Medical practices, clinics, and healthcare facilities',
      icon: Heart,
      url: '/industries/healthcare',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      name: 'Construction',
      description: 'Contractors, builders, and construction trades',
      icon: HardHat,
      url: '/industries/construction',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      name: 'Professional Services',
      description: 'Law firms, accounting, and consulting companies',
      icon: Scale,
      url: '/industries/professional-services',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      name: 'Technology',
      description: 'Software companies, startups, and tech firms',
      icon: Laptop,
      url: '/industries/technology',
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50'
    },
    {
      name: 'Manufacturing',
      description: 'Production facilities and manufacturing companies',
      icon: Cog,
      url: '/industries/manufacturing',
      color: 'text-gray-600',
      bgColor: 'bg-gray-50'
    },
    {
      name: 'Restaurant & Food Service',
      description: 'Restaurants, catering, and hospitality businesses',
      icon: ChefHat,
      url: '/industries/restaurant',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50'
    },
    {
      name: 'Retail',
      description: 'Stores, chains, and retail businesses',
      icon: Store,
      url: '/industries/retail',
      color: 'text-pink-600',
      bgColor: 'bg-pink-50'
    },
    {
      name: 'Non-Profit',
      description: 'Charitable organizations and foundations',
      icon: Users,
      url: '/industries/non-profit',
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-gray-50">
      <Helmet>
        <title>Industry-Specific PEO Services | Find PEOs by Industry | The PEO App</title>
        <meta name="description" content="Find PEO services specialized for your industry. Expert PEO solutions for healthcare, construction, technology, manufacturing, retail, and more industries." />
        <link rel="canonical" href="https://thepeoapp.com/industries" />
        <meta name="keywords" content="industry specific PEO, PEO by industry, specialized PEO services, healthcare PEO, construction PEO, technology PEO, manufacturing PEO" />
        
        <meta property="og:title" content="Industry-Specific PEO Services" />
        <meta property="og:description" content="Find PEO providers specialized for your industry with expert knowledge and tailored solutions." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thepeoapp.com/industries" />
      </Helmet>
      
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Industry-Specific PEO Services
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed max-w-3xl mx-auto">
              Find PEO providers who specialize in your industry and understand your unique challenges. 
              Get expert HR solutions tailored to your business sector.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-16">
            {industries.map((industry, index) => {
              const IconComponent = industry.icon;
              return (
                <Link 
                  key={index}
                  to={industry.url}
                  className="group block"
                >
                  <div className={`${industry.bgColor} p-6 rounded-xl hover:shadow-lg transition-all duration-200 group-hover:scale-105`}>
                    <IconComponent className={`h-12 w-12 ${industry.color} mb-4`} />
                    <h3 className="text-xl font-semibold mb-2 text-gray-900">
                      {industry.name}
                    </h3>
                    <p className="text-gray-700 text-sm">
                      {industry.description}
                    </p>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="bg-white p-8 rounded-xl shadow-lg">
            <h2 className="text-3xl font-bold mb-6 text-center">Why Choose Industry-Specific PEOs?</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="font-semibold mb-2">Industry Expertise</h3>
                <p className="text-gray-700 text-sm">
                  Deep understanding of your industry's specific regulations, challenges, and best practices.
                </p>
              </div>
              <div className="text-center">
                <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Scale className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="font-semibold mb-2">Specialized Compliance</h3>
                <p className="text-gray-700 text-sm">
                  Expert handling of industry-specific compliance requirements and regulatory changes.
                </p>
              </div>
              <div className="text-center">
                <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-8 w-8 text-purple-600" />
                </div>
                <h3 className="font-semibold mb-2">Tailored Solutions</h3>
                <p className="text-gray-700 text-sm">
                  Customized HR solutions designed specifically for your industry's unique needs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Industries;
